/**
 * Shared helpers. All provider calls go through the BYOK proxy at /api/proxy
 * so the user's key never appears in the client bundle and CORS quirks (e.g.
 * BytePlus, xAI) are smoothed over server-side.
 */
import type { GenerateResult } from '../router/types';

export interface ProxyRequest {
  provider: 'gemini' | 'openai' | 'xai' | 'anthropic' | 'byteplus' | 'elevenlabs' | 'meshy' | 'uniblock';
  endpoint: string;            // upstream URL, fully qualified
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  /** Provider key — proxied to Authorization header upstream. */
  apiKey: string;
  /** Optional: tell the proxy how to pass the key. Defaults to "bearer". */
  authStyle?: 'bearer' | 'x-api-key' | 'x-goog-api-key' | 'xi-api-key' | 'query';
  authQueryParam?: string;
  /** Extra headers forwarded verbatim (e.g. anthropic-version). */
  extraHeaders?: Record<string, string>;
}

export async function proxy<T = any>(req: ProxyRequest): Promise<T> {
  // In Node (Netlify Functions) we call upstream directly to avoid recursion.
  const isServer = typeof window === 'undefined';
  if (isServer) return directUpstream<T>(req);

  const r = await fetch('/api/proxy', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req)
  });
  const text = await r.text();
  let json: any;
  try { json = JSON.parse(text); } catch { json = { _raw: text }; }
  if (!r.ok) {
    const msg = json?.error || json?.message || `Proxy ${r.status}`;
    throw new ProviderError(msg, r.status, json);
  }
  return json as T;
}

async function directUpstream<T>(req: ProxyRequest): Promise<T> {
  const url = new URL(req.endpoint);
  const headers: Record<string, string> = { accept: 'application/json' };
  switch (req.authStyle ?? 'bearer') {
    case 'bearer':         headers['authorization'] = `Bearer ${req.apiKey}`; break;
    case 'x-api-key':      headers['x-api-key'] = req.apiKey; break;
    case 'x-goog-api-key': headers['x-goog-api-key'] = req.apiKey; break;
    case 'xi-api-key':     headers['xi-api-key'] = req.apiKey; break;
    case 'query':          url.searchParams.set(req.authQueryParam ?? 'key', req.apiKey); break;
  }
  let body: BodyInit | undefined;
  if (req.method !== 'GET' && req.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(req.body);
  }
  const r = await fetch(url.toString(), { method: req.method ?? 'POST', headers, body });
  const text = await r.text();
  let json: any;
  try { json = JSON.parse(text); } catch { json = { _raw: text }; }
  if (!r.ok) throw new ProviderError(json?.error || json?.message || `Upstream ${r.status}`, r.status, json);
  return json as T;
}

export class ProviderError extends Error {
  constructor(msg: string, public status: number, public body?: any) { super(msg); this.name = 'ProviderError'; }
}

/** Map an asset URL to a data URL, base64, or pass through. */
export async function urlToBase64(url: string): Promise<string> {
  if (url.startsWith('data:')) return url.split(',')[1];
  const r = await fetch(url);
  const blob = await r.blob();
  return await blobToBase64(blob);
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result).split(',')[1]);
    fr.onerror = () => rej(fr.error);
    fr.readAsDataURL(blob);
  });
}

export function mapAspectToSize(aspect?: string, base = 1024): { w: number; h: number } {
  switch (aspect) {
    case '1:1': return { w: base, h: base };
    case '3:4': return { w: Math.round(base * 0.75), h: base };
    case '4:3': return { w: base, h: Math.round(base * 0.75) };
    case '9:16': return { w: Math.round(base * 0.5625), h: base };
    case '16:9': return { w: base, h: Math.round(base * 0.5625) };
    case '21:9': return { w: base, h: Math.round(base * (9/21)) };
    default: return { w: base, h: base };
  }
}

export const ok = (assets: GenerateResult['assets']): GenerateResult => ({ status: 'succeeded', assets });
export const queued = (taskId: string, raw?: unknown): GenerateResult => ({ status: 'queued', taskId, raw });
export const fail = (error: string, raw?: unknown): GenerateResult => ({ status: 'failed', error, raw });
